import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { KeyRound } from 'lucide-react';
import {
    listTenantSecretValueKeys,
    setTenantSecretValue,
    deleteTenantSecretValue,
    setTenantSecret,
    deleteTenantSecret,
} from '@/lib/tenant-overrides';

interface ProviderApiKeysProps {
    providerRef: string;
    appKey: string;
}

interface SecretKeyEntry {
    name: string;
    hasValue: boolean;
}

export function ProviderApiKeys({ providerRef, appKey }: ProviderApiKeysProps) {
    const [entries, setEntries] = useState<SecretKeyEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState('');

    const providerFqcn = providerRef.includes('/')
        ? providerRef
        : appKey
            ? `${appKey}/${providerRef}`
            : providerRef;

    const KNOWN_PROVIDER_KEYS: Record<string, string[]> = {
        'provider.llm.openai': ['OPENAI_API_KEY'],
        'provider.llm.gemini': ['GEMINI_API_KEY'],
    };

    const refresh = useCallback(async () => {
        if (!providerRef) return;
        setLoading(true);
        setError(null);
        try {
            const storedKeys = await listTenantSecretValueKeys({ cap: providerFqcn });
            const storedSet = new Set(storedKeys);

            const providerName = providerRef.includes('/')
                ? providerRef.split('/').pop() ?? providerRef
                : providerRef;
            const knownKeys = KNOWN_PROVIDER_KEYS[providerName] ?? [];

            const allKeys = Array.from(new Set([...knownKeys, ...storedKeys]));
            setEntries(allKeys.map(k => ({ name: k, hasValue: storedSet.has(k) })));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [providerRef, providerFqcn]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const onSave = async (key: string) => {
        if (!editingValue.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await setTenantSecretValue({ cap: providerFqcn, key, value: editingValue });
            await setTenantSecret({ cap: providerFqcn, key, provider: 'registry', ref: key });
            setEditingKey(null);
            setEditingValue('');
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (key: string) => {
        setBusy(true);
        setError(null);
        try {
            await deleteTenantSecretValue({ cap: providerFqcn, key });
            await deleteTenantSecret({ cap: providerFqcn, key });
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    if (!providerRef) return null;

    return (
        <Card className="p-6">
            <header className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <KeyRound size={20} className="text-amber-600" />
                </div>
                <div>
                    <h3 className="text-base font-semibold text-slate-900">Provider API Keys</h3>
                    <p className="text-sm text-slate-500">
                        API keys for the selected provider. Values are encrypted at rest and
                        resolved at invocation time.
                    </p>
                </div>
            </header>

            {error && (
                <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                </div>
            )}

            {loading ? (
                <p className="text-sm text-slate-500">Loading…</p>
            ) : entries.length === 0 ? (
                <p className="text-sm text-slate-500">No API keys declared for this provider.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {entries.map((entry) => {
                        const isEditing = editingKey === entry.name;
                        return (
                            <li key={entry.name} className="py-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm font-semibold text-slate-800">
                                            {entry.name}
                                        </span>
                                        {entry.hasValue ? (
                                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                                                configured
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                                                not set
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={busy}
                                            onClick={() => {
                                                if (isEditing) {
                                                    setEditingKey(null);
                                                    setEditingValue('');
                                                } else {
                                                    setEditingKey(entry.name);
                                                    setEditingValue('');
                                                }
                                            }}
                                        >
                                            {isEditing ? 'Cancel' : entry.hasValue ? 'Update' : 'Set key'}
                                        </Button>
                                        {entry.hasValue && !isEditing && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                disabled={busy}
                                                onClick={() => onDelete(entry.name)}
                                                className="text-rose-600 hover:text-rose-700"
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {isEditing && (
                                    <div className="mt-2 flex items-end gap-2">
                                        <div className="flex-1">
                                            <Label className="text-xs font-medium text-slate-600">
                                                API Key
                                            </Label>
                                            <Input
                                                type="password"
                                                value={editingValue}
                                                onChange={(e) => setEditingValue(e.target.value)}
                                                placeholder="Enter API key…"
                                                className="mt-1 font-mono"
                                                autoFocus
                                            />
                                        </div>
                                        <Button
                                            onClick={() => onSave(entry.name)}
                                            disabled={busy || !editingValue.trim()}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </Card>
    );
}
